import * as _ from 'lodash'
import { Variable } from 'alkali'

const newline = '\r\n'
const delimiter = ','

const fetch = (store: StoreOrArray) => Promise.resolve(typeof store.fetch === 'function' ? store.fetch() : store)

type RowBuilder = (item: any, emit: (cells: any[]) => void, defaultRowBuilder?: RowBuilder) => void
type RowsProcessor = (rows: any[], headers: string[], fields: string[]) => any[]
export interface ExportOptions {
	excludeHiddenColumns?: boolean,
	buildRow?: RowBuilder,
	processRows?: RowsProcessor
	escape?: RegExp | ((value: string) => boolean) | boolean
}
type StoreOrArray = { fetch: () => Promise<any[]> } | any[]

/**
 * Accepts a resolvable array of row data, a list of field descriptors
 * @param {Store | Array} collection
 * @param {Object[] | string[]} fieldNames
 * @param {{}} options
 * @returns {Promise<string[]>}
 */
function exportCsv(collection: StoreOrArray, fieldNames: object[] | string[], options: ExportOptions = {}): Promise<string|void> {
	const headers = []
	const fields = []
	const getCellData = []
	let prefix = ''

	const ignoredField = f => f === 'selector' || f === 'Debugger'

	_.forEach(fieldNames, (fieldName) => {
		// no cases of this in the app any more, but it's possible
		if (typeof fieldName === 'string') {
			if (ignoredField(fieldName)) {
				return true
			}

			headers.push(fieldName)
			fields.push(fieldName)
			getCellData.push(_.get)

		} else if (_.isPlainObject(fieldName)) {
			if (fieldName.header) {
				if (fieldName.headerEnd) {
					prefix = ''
				} else {
					prefix = fieldName.label + ' '
				}
				return
			}
			if (fieldName.exportable == false ||
				(options.excludeHiddenColumns && fieldName.hidden)) {
				return
			}

			const header = prefix + (fieldName.exportLabel || fieldName.label || fieldName.field || fieldName.id)
			const field = (fieldName.field != null) ? fieldName.field :
				(fieldName.id != null) ? fieldName.id : fieldName.label

			if (ignoredField(field)) {
				return true
			}

			headers.push(header)
			fields.push(field)
			getCellData.push((fieldName.getForExport && fieldName.getForExport.bind(fieldName)) ||
					(fieldName.get && fieldName.get.bind(fieldName)) || _.get)
		}
	})

	function defaultBuildRow(item, emit) {
		const cells =
			fields.map((fieldName, index) => getCellData[index](item, fieldName))

		emit(cells)
	}

	const buildRow = options.buildRow || defaultBuildRow

	return fetch(collection).then((data) => {
		const header = headers.map(v => serializeValue(v, options.escape)).join(delimiter) + newline
		let csvRows = []

		for (const row of data) {
			buildRow(row, (cells) => {
				csvRows.push(cells)
			}, defaultBuildRow)
		}

		if (options.processRows) {
			csvRows = options.processRows(csvRows, headers, fields)
		}

		return header + csvRows.map(cells => cells.map(v => serializeValue(v, options.escape)).join(delimiter)).join(newline)
	}).then(null, (error) => {
		window.onerror(error)
	})
}

function serializeValue(value, escape) {
	if (value instanceof Variable) {
		value = value.valueOf()
	}

	// null or undefined
	if (value == null) {
		return ''
	}

	if (typeof value === 'number') {
		// just convert to string, no quotes necessary
		return value + ''
	}

	// convert to a string, then do quoting for anything else
	if (value && value instanceof Set) {
		value = Array.from(value).join(', ')
	} else if (Array.isArray(value)) {
		value = value.join(', ')
	} else if (typeof value !== 'string') {
		value = value + ''
	}

	let quoted = '"' + value.replace(/"/g, '""') + '"'

	// need to quote it, and possibly convert it to a formula to avoid excel transformation
	// if it starts with = or 0, has a - followed by a digit, and doesn't have letters
	let needsFormulaEmbedding

	if (escape !== false) {
		if (escape instanceof RegExp) {
			needsFormulaEmbedding = escape.test(value)
		} else if (_.isFunction(escape)) {
			needsFormulaEmbedding = escape
		} else {
			needsFormulaEmbedding = (value.charAt(0) === '=' ||
				(value.match(/^0|.-\s*\d/) && !value.match(/[a-z_A-Z]/))) ||
				(value.charAt(0).match(/[0-9]/) && value.indexOf('/') > -1) ||
				value.trim().match(/^\(.*\)$/)
		}
	}

	if (needsFormulaEmbedding) {
		quoted = '"="' + quoted + '""'
	}

	return quoted
}

export default exportCsv
